import { useEffect, useMemo, useRef, useState } from 'react';
import { Bell, Loader2 } from 'lucide-react';
import { api } from '../services/api';

const isNotificationSupported = typeof window !== 'undefined' && 'Notification' in window;

const formatCurrency = (value) => {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return 'R$ 0,00';
  }
  return number.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

const buildDueLabel = (notification) => {
  if (typeof notification?.daysUntilDue === 'number') {
    const diff = notification.daysUntilDue;
    if (diff < 0) {
      const abs = Math.abs(diff);
      return abs === 1 ? 'Vencido há 1 dia' : `Vencido há ${abs} dias`;
    }
    if (diff === 0) return 'Vence hoje';
    if (diff === 1) return 'Vence amanhã';
    return `Vence em ${diff} dias`;
  }

  if (notification?.dueDate) {
    const date = new Date(notification.dueDate);
    if (!Number.isNaN(date.getTime())) {
      return `Vencimento: ${date.toLocaleDateString('pt-BR')}`;
    }
  }

  return null;
};

const requestNotificationPermission = () => {
  if (!isNotificationSupported) return;
  if (Notification.permission === 'default') {
    try {
      Notification.requestPermission();
    } catch (error) {
      console.warn('Não foi possível solicitar permissão de notificações:', error);
    }
  }
};

const triggerBrowserNotification = (notification, onClick) => {
  if (!isNotificationSupported || Notification.permission !== 'granted') {
    return;
  }

  try {
    const browserNotification = new Notification(notification.title || 'Alerta financeiro', {
      body: notification.message,
      tag: `finance-alert-${notification.id}`,
    });

    browserNotification.onclick = () => {
      if (typeof onClick === 'function') {
        onClick();
      }
      window.focus();
      browserNotification.close();
    };
  } catch (error) {
    console.warn('Falha ao exibir notificação do navegador:', error);
  }
};

const NotificationCenter = ({ onNavigate, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);
  const notifiedRef = useRef(new Set());
  const intervalRef = useRef(null);

  const unreadCount = useMemo(() => items.length, [items]);

  const fetchNotifications = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
        setError('');
      }
      const response = await api.getPendingNotifications();
      const notifications = response?.notifications || [];
      setItems(notifications);

      const fresh = notifications.filter((notification) => !notifiedRef.current.has(notification.id));
      fresh.forEach((notification) => {
        notifiedRef.current.add(notification.id);
        triggerBrowserNotification(notification, () => {
          if (onNavigate) {
            onNavigate(notification.filters || {}, notification);
          }
          api.markNotificationAsRead(notification.id).catch(() => {});
        });
      });
    } catch (err) {
      console.error('Erro ao carregar notificações pendentes:', err);
      if (!silent) {
        setError('Não foi possível carregar as notificações.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    requestNotificationPermission();
    fetchNotifications({ silent: false });
    intervalRef.current = setInterval(() => fetchNotifications({ silent: true }), 60000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleNotificationClick = async (notification) => {
    setOpen(false);
    try {
      await api.markNotificationAsRead(notification.id);
      setItems((prev) => prev.filter((item) => item.id !== notification.id));
    } catch (err) {
      console.error('Erro ao marcar notificação como lida:', err);
    }

    if (onNavigate) {
      onNavigate(notification.filters || {}, notification);
    }
  };

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      fetchNotifications({ silent: true });
    }
  };

  const handleClearAll = async () => {
    const current = [...items];
    setItems([]);
    setOpen(false);

    await Promise.all(
      current.map((notification) =>
        api.markNotificationAsRead(notification.id).catch((error) => {
          console.error('Erro ao marcar notificação como lida:', error);
        })
      )
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={handleToggle}
        className={`relative flex items-center justify-center rounded-full transition-colors ${
          compact
            ? 'w-10 h-10 bg-white/80 dark:bg-gray-800/80 hover:bg-primary-50 dark:hover:bg-gray-700'
            : 'w-11 h-11 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm hover:border-primary-300'
        }`}
        aria-label="Notificações"
      >
        <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-primary-600 text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-3 w-80 max-w-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Notificações</h3>
            <button
              type="button"
              onClick={() => fetchNotifications({ silent: false })}
              className="text-xs font-medium text-primary-600 hover:underline"
            >
              Atualizar
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto p-3 space-y-3 custom-scrollbar">
            {loading ? (
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Carregando notificações...</span>
              </div>
            ) : error ? (
              <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>
            ) : items.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                Nenhuma notificação pendente.
              </div>
            ) : (
              items.map((notification) => {
                const dueLabel = buildDueLabel(notification);
                const counterpart = notification.tipo === 'pagar' ? notification.fornecedor : notification.cliente;

                return (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() => handleNotificationClick(notification)}
                    className="w-full text-left p-3 bg-gray-50 dark:bg-gray-800/60 hover:bg-primary-50 dark:hover:bg-gray-700 rounded-lg transition"
                  >
                    <p className="text-sm font-semibold text-gray-900 dark:text-white leading-snug">
                      {notification.title || 'Alerta'}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 leading-relaxed">
                      {notification.message}
                    </p>
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                      {counterpart && <p>{counterpart}</p>}
                      {notification.valor !== undefined && notification.valor !== null && (
                        <p>{formatCurrency(notification.valor)}</p>
                      )}
                      {dueLabel && <p className="text-primary-600 dark:text-primary-300 font-medium">{dueLabel}</p>}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {items.length > 0 && !loading && (
            <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs font-medium text-danger-600 dark:text-danger-400 hover:underline"
              >
                Limpar tudo
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
